import React from 'react';
import { Box, Button, Chip, ListItemText, Menu, MenuItem, Typography } from '@mui/material';

export interface IdentifierCandidate {
  upc?: string | null;
  ean?: string | null;
  gtin?: string | null;
  mpn?: string | null;
  title?: string | null;
  source: string;
  confidence: number;
  matchReason?: string | null;
}

/** The barcode a candidate would put on the row, UPC first. */
export function candidateBarcode(candidate: IdentifierCandidate): string | null {
  for (const v of [candidate.upc, candidate.ean, candidate.gtin]) {
    const s = String(v || '').trim();
    if (s) return s;
  }
  return null;
}

interface Props {
  candidates: IdentifierCandidate[];
  /** Barcode already on the row; its own candidate offers no Use. */
  currentBarcode?: string | null;
  disabled?: boolean;
  /** Called with the candidate and the barcode it carries. */
  onUse: (candidate: IdentifierCandidate, barcode: string) => void;
}

/**
 * The "N candidates" chip of the UPC review queue. Enrichment and the bulk
 * gap pass stage lookup results in `identifierCandidates` without writing a
 * barcode; this is where a person reads them and accepts one with a click.
 */
export const IdentifierCandidatesChip: React.FC<Props> = ({
  candidates,
  currentBarcode,
  disabled,
  onUse,
}) => {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const count = candidates.length;
  const current = String(currentBarcode || '').trim();

  return (
    <>
      <Chip
        label={`${count} candidate${count === 1 ? '' : 's'}`}
        size="small"
        variant="outlined"
        clickable={count > 0}
        onClick={count > 0 ? (e) => setAnchor(e.currentTarget) : undefined}
        aria-haspopup={count > 0 ? 'menu' : undefined}
      />
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { maxWidth: 560 } } }}
      >
        {candidates.map((c, i) => {
          const barcode = candidateBarcode(c);
          const rejected = /rejected/i.test(c.matchReason || '');
          // An ungrounded model answer is a guess, not a lookup; say so beside the number.
          const guess = c.source === 'gemini_ai';
          const usable = !!barcode && barcode !== current && !disabled;
          return (
            <MenuItem key={`${c.source}-${barcode || c.mpn || i}`} disableRipple sx={{ alignItems: 'flex-start', gap: 1.5, whiteSpace: 'normal' }}>
              <ListItemText
                primary={
                  <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography component="span" variant="body2" fontWeight={600}>
                      {barcode || 'No barcode'}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary">
                      {Math.round((c.confidence || 0) * 100)}% · {c.source}
                      {c.mpn ? ` · MPN ${c.mpn}` : ''}
                      {guess ? ' · AI guess, unverified' : ''}
                      {rejected ? ' · rejected' : ''}
                    </Typography>
                  </Box>
                }
                secondary={[c.title, c.matchReason].filter(Boolean).join(' — ')}
                secondaryTypographyProps={{ variant: 'caption', sx: { display: 'block', maxWidth: 420 } }}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={!usable}
                onClick={() => {
                  if (!barcode) return;
                  setAnchor(null);
                  onUse(c, barcode);
                }}
                aria-label={barcode ? `Use ${barcode}` : 'No barcode to use'}
              >
                {barcode && barcode === current ? 'On row' : 'Use'}
              </Button>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
